import { useState } from 'react';
import { CheckIcon, CopyIcon } from 'lucide-react';
import { inviteUserRequest } from '@redirector/shared';
import { Button } from '../ui/Button.js';
import { Callout } from '../ui/Callout.js';
import { Dialog } from '../ui/Dialog.js';
import { Field } from '../ui/Field.js';
import { Input } from '../components/ui/input.js';
import { useInviteUser } from './queries.js';

export type InviteDialogProps = { open: boolean; onClose: () => void };

/** How long the copy button stays in its confirmed state. */
const COPIED_NOTICE_MS = 1500;

/**
 * Invites somebody in two steps: name the address, then hand over the link.
 *
 * The second step is the whole reason this is a dialog and not a form that
 * closes on submit. The link is shown exactly once — the server keeps only its
 * hash — so closing this before copying it means issuing a new invitation.
 * Nothing here asks for a password: the recipient sets their own, which is the
 * only way an account can get one.
 */
export function InviteDialog({ open, onClose }: InviteDialogProps) {
  const invite = useInviteUser();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  function reset() {
    setEmail('');
    setError(null);
    setLink(null);
    setCopied(false);
    invite.reset();
    onClose();
  }

  async function submit() {
    setError(null);
    // The very schema the server validates with.
    const parsed = inviteUserRequest.safeParse({ email });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Enter an email address');
      return;
    }

    try {
      const created = await invite.mutateAsync(parsed.data);
      // An absolute URL, because this is going into a message to somebody
      // else: a bare path is useless once it leaves the browser.
      setLink(`${window.location.origin}${created.invitePath}`);
    } catch (thrown) {
      setError(thrown instanceof Error ? thrown.message : 'Could not invite that person');
    }
  }

  return (
    <Dialog open={open} title={link === null ? 'Invite a user' : 'Send this link'} onClose={reset}>
      {link === null ? (
        <div className="flex flex-col gap-(--space-4)">
          <p className="text-[length:var(--text-small)] font-medium text-ink-muted">
            They will get a one-time link and choose their own password. Nobody else ever sets it.
          </p>

          {error !== null && <Callout tone="danger">{error}</Callout>}

          <Field label="Email">
            {({ id, invalid }) => (
              <Input
                id={id}
                type="email"
                autoComplete="off"
                autoFocus
                aria-invalid={invalid}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') void submit();
                }}
              />
            )}
          </Field>

          <div className="flex justify-end gap-(--space-2)">
            <Button variant="secondary" onClick={reset} disabled={invite.isPending}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => void submit()} disabled={invite.isPending}>
              {invite.isPending ? 'Inviting…' : 'Invite'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-(--space-4)">
          <Callout tone="warn">
            Copy this now. It is shown once and cannot be looked up again — if it is lost, invite
            them again to issue a new one. It stops working after seven days, or as soon as it is
            used.
          </Callout>

          <div className="flex items-center gap-(--space-2)">
            <label className="sr-only" htmlFor="invite-link">
              Invitation link
            </label>
            <Input
              id="invite-link"
              readOnly
              className="font-mono text-[length:var(--text-small)]"
              value={link}
              onFocus={(event) => event.currentTarget.select()}
            />
            <Button
              variant="secondary"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(link);
                  setCopied(true);
                  setTimeout(() => setCopied(false), COPIED_NOTICE_MS);
                } catch {
                  // Clipboard access can be denied; the link is on screen.
                }
              }}
            >
              {copied ? (
                <>
                  <CheckIcon aria-hidden="true" className="text-ok" /> Copied
                </>
              ) : (
                <>
                  <CopyIcon aria-hidden="true" /> Copy
                </>
              )}
            </Button>
          </div>

          <div className="flex justify-end">
            <Button variant="primary" onClick={reset}>
              Done
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
